import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Error,
  Warning,
  ArrowForward,
  ChevronRight,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../landingPage/Sidebar';
import { PrimaryBreadcrumb } from '../../components/ui/breadcrumb';
import { PrimaryButton } from '../../components/ui/button';
import { PrimaryTabs } from '../../components/ui/tabs';
import successIcon from './images/SuccessIcon.png';
import failedIcon from './images/FailedIcon.png';
import partialSuccessIcon from './images/partialSuccessIcon.png';
import salesforceIcon from './images/SalesforceIcon (2).png';
import transformIcon from './images/TransformIcon.png';
import postgresIcon from './images/postgresIcon.png';
import reexecuteIcon from './images/ReexecuteIcon.png';

const statusChipPalette = {
  Success: { bgLight: '#DCFCE7', text: '#008236', border: '#008236', icon: successIcon },
  Failed: { bgLight: '#FFE2E2', text: '#C10007', border: '#C10007', icon: failedIcon },
  Running: { bgLight: '#DBEAFE', text: '#1447E6', border: '#1447E6', icon: successIcon },
  'Partial Success': { bgLight: '#FFEDD4', text: '#D87000', border: '#D87000', icon: partialSuccessIcon },
};

const PipelineRunDetails = () => {
  const navigate = useNavigate();
  const { runId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [expandedEntities, setExpandedEntities] = useState({});

  // TODO: Use runId to fetch actual data from API when backend is ready
  // For now, using hardcoded data matching the image
  // eslint-disable-next-line no-unused-vars
  const _runId = runId;

  // Hardcoded data matching the image
  const runData = {
    id: runId || 'RUN-20260119-001',
    pipelineName: 'Salesforce to Postgres',
    status: 'Failed',
    startTime: '2026-01-19 10:32:15',
    duration: '1m 57s',
    recordsIn: 12000,
    recordsOut: 11700,
    executionFlow: [
      {
        name: 'Salesforce',
        type: 'Source',
        status: 'Success',
        recordsProcessed: 12000,
        connectorIcon: salesforceIcon,
        statusIcon: successIcon,
        bgColor: '#DCFCE7',
        borderColor: '#008236',
        textColor: '#008236',
      },
      {
        name: 'Transform & Map',
        type: 'Source',
        status: 'Warning',
        recordsProcessed: 11800,
        connectorIcon: transformIcon,
        statusIcon: partialSuccessIcon,
        bgColor: '#FFEDD4',
        borderColor: '#D87000',
        textColor: '#D87000',
      },
      {
        name: 'Postgres',
        type: 'Destination',
        status: 'Failed',
        recordsProcessed: 0,
        reason: 'Constraint violation',
        connectorIcon: postgresIcon,
        statusIcon: failedIcon,
        bgColor: '#FFF5F5',
        borderColor: '#C10007',
        textColor: '#C10007',
      },
    ],
    entities: [
      {
        name: 'Accounts',
        status: 'Success',
        records: 5000,
        errors: [],
      },
      {
        name: 'Contacts',
        status: 'Failed',
        records: 6800,
        errors: [
          {
            message: 'Duplicate key value violates unique constraint "contacts_email_key"',
            destination: 'Destination',
            row: 6801,
            field: 'email',
            step: 'Postgres Destination',
            note: 'Constraint violation',
          },
          {
            message: 'Required field mapping missing',
            step: 'Transform',
            field: 'owner_id',
            row: 6802,
          },
        ],
      },
      {
        name: 'Opportunities',
        status: 'Success',
        records: 3200,
        errors: [],
      },
    ],
    logs: [
      '[2026-01-19 10:32:15] Pipeline execution started',
      '[2026-01-19 10:32:16] Connecting to Salesforce...',
      '[2026-01-19 10:32:17] Successfully authenticated with Salesforce',
      '[2026-01-19 10:32:18] Fetching Accounts: 0/5000',
      '[2026-01-19 10:32:45] Fetching Accounts: 5000/5000 (Complete)',
      '[2026-01-19 10:33:00] Fetching Contacts: 0/6800',
      '[2026-01-19 10:33:30] Fetching Contacts: 6800/6800 (Complete)',
      '[2026-01-19 10:33:45] Fetching Opportunities: 0/3200',
      '[2026-01-19 10:34:00] Fetching Opportunities: 3200/3200 (Complete)',
      '[2026-01-19 10:34:15] Starting transformation...',
      '[2026-01-19 10:34:30] Transformation complete',
      '[2026-01-19 10:34:45] Writing to Postgres...',
      '[2026-01-19 10:35:12] Error: Constraint violation in Contacts',
    ],
    errors: [
      {
        entity: 'Contacts',
        message: 'Duplicate key value violates unique constraint "contacts_email_key"',
        destination: 'Destination',
        row: 6801,
        field: 'email',
        step: 'Postgres Destination',
        note: 'Constraint violation',
      },
      {
        entity: 'Contacts',
        message: 'Required field mapping missing',
        step: 'Transform',
        field: 'owner_id',
        row: 6802,
      },
    ],
    warnings: [
      {
        entity: 'Transform & Map',
        message: 'Some records skipped during transformation',
        count: 200,
      },
    ],
    metrics: {
      totalRecords: 12000,
      successfulRecords: 11700,
      failedRecords: 300,
      successRate: 97.5,
      avgProcessingTime: '0.15s',
    },
  };

  const handleBack = () => {
    navigate('/pipeline');
  };

  const toggleEntity = (index) => {
    setExpandedEntities((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderStatusChip = (status) => {
    const palette = statusChipPalette[status] || statusChipPalette.Failed;
    return (
      <Chip
        icon={
          <Box
            component="img"
            src={palette.icon}
            alt={status}
            sx={{ width: 14, height: 14 }}
          />
        }
        label={status}
        sx={{
          height: 28,
          fontSize: '0.75rem',
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          bgcolor: palette.bgLight,
          color: palette.text,
          border: `1px solid ${palette.border}`,
          borderRadius: '6px',
          '& .MuiChip-icon': {
            marginLeft: '8px',
            marginRight: '0',
          },
        }}
      />
    );
  };

  const tabs = [
    { label: 'Logs' },
    { label: `Errors ${runData.errors.length}`, count: runData.errors.length },
    { label: 'Warnings', count: runData.warnings.length },
    { label: 'Metrics' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        minHeight: '100vh',
      }}
    >
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1, sm: 1.5 }, bgcolor: '#F1F5FF' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                p: 0.5,
                color: '#525252',
                '&:hover': { bgcolor: '#F3F4F6' },
              }}
            >
              <ArrowBack sx={{ fontSize: 20 }} />
            </IconButton>
            <PrimaryBreadcrumb
              items={[
                { label: 'Pipeline Runs', onClick: handleBack },
                { label: runData.pipelineName },
              ]}
              sx={{ mb: 0 }}
            />
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 }, maxWidth: '100%', overflowX: 'hidden', bgcolor: ' #FAFBFD' }}>
          {/* Pipeline Run Summary */}
          <Card
            sx={{
              mb: 1.5,
              // borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              {/* First Row: Status Chip, Pipeline Name, and Retry Buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {renderStatusChip(runData.status)}
                  <Typography
                    sx={{
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      fontWeight: 600,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {runData.pipelineName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <PrimaryButton
                    variant="outlined"
                    sx={{ fontSize: '0.8125rem', px: 2 }}
                    startIcon={
                      <Box
                        component="img"
                        src={reexecuteIcon}
                        alt="Retry"
                        sx={{ width: 16, height: 16 }}
                      />
                    }
                  >
                    Retry Failed Entities
                  </PrimaryButton>
                  <PrimaryButton
                    variant="contained"
                    sx={{ fontSize: '0.8125rem', px: 2 }}
                    startIcon={
                      <Box
                        component="img"
                        src={reexecuteIcon}
                        alt="Retry"
                        sx={{ width: 16, height: 16 }}
                      />
                    }
                  >
                    Retry Entire Pipeline
                  </PrimaryButton>
                </Box>
              </Box>

              {/* Run ID */}
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: '#6B7280',
                  fontFamily: 'Inter, sans-serif',
                  mb: 1.5,
                }}
              >
                {runData.id}
              </Typography>

              {/* Parameters with Dividers */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 0 }}>
                <Box sx={{ flex: 1, py: 1, pr: { xs: 0, sm: 2 }, borderRight: { xs: 'none', sm: `1px solid #929292` }, mb: { xs: 1, sm: 0 }, pb: { xs: 1, sm: 1 }, borderBottom: { xs: `1px solid #929292`, sm: 'none' } }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      mb: 0.5,
                    }}
                  >
                    Start Time
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {runData.startTime}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, py: 1, px: { xs: 0, sm: 2 }, borderRight: { xs: 'none', sm: `1px solid #929292` }, mb: { xs: 1, sm: 0 }, pb: { xs: 1, sm: 1 }, borderBottom: { xs: `1px solid #929292`, sm: 'none' } }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      mb: 0.5,
                    }}
                  >
                    Duration
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {runData.duration}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, py: 1, px: { xs: 0, sm: 2 }, borderRight: { xs: 'none', sm: `1px solid #929292` }, mb: { xs: 1, sm: 0 }, pb: { xs: 1, sm: 1 }, borderBottom: { xs: `1px solid #929292`, sm: 'none' } }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      mb: 0.5,
                    }}
                  >
                    Records In
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {runData.recordsIn.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, py: 1, pl: { xs: 0, sm: 2 } }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      mb: 0.5,
                    }}
                  >
                    Records Out
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {runData.recordsOut.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Execution Flow */}
          <Card
            sx={{
              mb: 1.5,
              // borderRadius: '4px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#0F172A',
                  fontFamily: 'Inter, sans-serif',
                  mb: 2,
                }}
              >
                Execution Flow
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: { xs: 1.5, md: 1.5 },
                }}
              >
                {runData.executionFlow.map((stage, index) => (
                  <React.Fragment key={index}>
                    <Box
                      sx={{
                        height: '130px',
                        borderRadius: '8px',
                        border: `1px solid ${stage.status === 'Failed' ? stage.borderColor : `${stage.borderColor}33`}`,
                        bgcolor: stage.bgColor,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '250px',
                        p: 1.5,
                        // flexShrink: 1,
                      }}
                    >
                      {/* Status Icon in Top Right */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                      >
                        <Box
                          component="img"
                          src={stage.statusIcon}
                          alt={stage.status}
                          sx={{ width: 20, height: 20 }}
                        />
                      </Box>

                      {/* Connector Icon with White Background and Border */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          bgcolor: 'white',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          mb: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src={stage.connectorIcon}
                          alt={stage.name}
                          sx={{ width: 32, height: 32 }}
                        />
                      </Box>

                      {/* Labels */}
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: '#0F172A',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: 1.2,
                            mb: 0.25,
                          }}
                        >
                          {stage.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: 1.2,
                          }}
                        >
                          {stage.type}
                        </Typography>
                      </Box>

                      {/* Records Processed */}
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: stage.reason ? 1 : 0,
                        }}
                      >
                        Records Processed: {stage.recordsProcessed.toLocaleString()}
                      </Typography>

                      {/* Divider and Error Message */}
                      {stage.reason && (
                        <>
                          <Divider sx={{ my: 1, borderColor: '#E5E7EB' }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              component="img"
                              src={failedIcon}
                              alt="Error"
                              sx={{ width: 14, height: 14 }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: stage.textColor,
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                              }}
                            >
                              {stage.reason}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                    {index < runData.executionFlow.length - 1 && (
                      <Box
                        sx={{
                          display: { xs: 'none', md: 'flex' },
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9CA3AF',
                          minWidth: '80px',
                          width: '80px',
                          position: 'relative',
                          mx: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            height: '2px',
                            bgcolor: '#9CA3AF',
                            zIndex: 0,
                          }}
                        />
                        <ArrowForward sx={{ fontSize: 30, zIndex: 1, bgcolor: '#FFF', px: 0.5, position: 'relative' }} />
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Entity Breakdown */}
          <Card
            sx={{
              mb: 1.5,
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#0F172A',
                  fontFamily: 'Inter, sans-serif',
                  mb: 0.5,
                }}
              >
                Entity Breakdown
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  fontFamily: 'Inter, sans-serif',
                  mb: 2,
                }}
              >
                Detailed status for each entity in this pipeline.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {runData.entities.map((entity, index) => {
                  const isExpanded = expandedEntities[index] || (entity.errors.length > 0 && index === 1);
                  return (
                    <Box key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: isExpanded && entity.errors.length > 0 ? 0.75 : 0,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          {entity.errors.length > 0 && (
                            <IconButton
                              onClick={() => toggleEntity(index)}
                              sx={{
                                p: 0.5,
                                color: '#6B7280',
                                '&:hover': { bgcolor: 'transparent' },
                              }}
                            >
                              {isExpanded ? (
                                <ExpandMore sx={{ fontSize: 20 }} />
                              ) : (
                                <ChevronRight sx={{ fontSize: 20 }} />
                              )}
                            </IconButton>
                          )}
                          {entity.status === 'Success' ? (
                            <Box
                              component="img"
                              src={successIcon}
                              alt="Success"
                              sx={{ width: 20, height: 20 }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={failedIcon}
                              alt="Failed"
                              sx={{ width: 20, height: 20 }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {entity.name}
                          </Typography>
                          <Box sx={{ ml: 0.5 }}>
                            {renderStatusChip(entity.status)}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#525252',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {entity.records.toLocaleString()} records
                          </Typography>
                          {entity.errors.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: '0.8125rem',
                                color: '#C10007',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                              }}
                            >
                              {entity.errors.length} errors
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#1447E6',
                              fontFamily: 'Inter, sans-serif',
                              cursor: 'pointer',
                            }}
                          >
                            View Logs
                          </Typography>
                        </Box>
                      </Box>
                      {isExpanded && entity.errors.length > 0 && (
                        <Box sx={{ ml: 4, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {entity.errors.map((error, errorIndex) => (
                            <Paper
                              key={errorIndex}
                              sx={{
                                p: 1.5,
                                bgcolor: '#FFF5F5',
                                border: '1px solid #FFE2E2',
                                borderRadius: '6px',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                <Box
                                  component="img"
                                  src={failedIcon}
                                  alt="Error"
                                  sx={{ width: 18, height: 18, mt: 0.25 }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.8125rem',
                                    color: '#0F172A',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    flex: 1,
                                  }}
                                >
                                  {error.message}
                                </Typography>
                              </Box>
                              <Box sx={{ ml: 3.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {error.destination && (
                                  <Typography
                                    sx={{
                                      fontSize: '0.75rem',
                                      color: '#6B7280',
                                      fontFamily: 'Inter, sans-serif',
                                    }}
                                  >
                                    {error.destination}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  {error.step && (
                                    <Typography
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: '#6B7280',
                                        fontFamily: 'Inter, sans-serif',
                                      }}
                                    >
                                      Step: {error.step}
                                    </Typography>
                                  )}
                                  {error.field && (
                                    <Typography
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: '#6B7280',
                                        fontFamily: 'Inter, sans-serif',
                                      }}
                                    >
                                      Field: {error.field}
                                    </Typography>
                                  )}
                                  {error.row && (
                                    <Typography
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: '#6B7280',
                                        fontFamily: 'Inter, sans-serif',
                                      }}
                                    >
                                      Row: {error.row}
                                    </Typography>
                                  )}
                                </Box>
                                {error.note && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Box
                                      component="img"
                                      src={failedIcon}
                                      alt="Constraint violation"
                                      sx={{ width: 14, height: 14 }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: '#C10007',
                                        fontFamily: 'Inter, sans-serif',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {error.note}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                      {index < runData.entities.length - 1 && (
                        <Divider sx={{ mt: 1, borderColor: '#E5E7EB' }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>

          {/* Tabs Section */}
          <Card
            sx={{
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              <PrimaryTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabs={tabs.map((tab) => ({
                  label: tab.count !== undefined ? `${tab.label} ${tab.count}` : tab.label,
                }))}
              />
              <Divider sx={{ mb: 1.5, borderColor: '#E5E7EB' }} />

              {/* Logs Tab */}
              {activeTab === 0 && (
                <Box
                  sx={{
                    maxHeight: '350px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#F3F4F6',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#D1D5DB',
                      borderRadius: '3px',
                      '&:hover': {
                        background: '#9CA3AF',
                      },
                    },
                  }}
                >
                  {runData.logs.map((log, index) => {
                    const isError = log.includes('ERROR') || log.includes('failed');
                    return (
                      <Typography
                        key={index}
                        sx={{
                          fontSize: '0.8125rem',
                          color: isError ? '#C10007' : '#525252',
                          fontFamily: 'monospace',
                          py: 0.5,
                        }}
                      >
                        {log}
                      </Typography>
                    );
                  })}
                </Box>
              )}

              {/* Errors Tab */}
              {activeTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {runData.errors.map((error, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 1.5,
                        bgcolor: '#FFF5F5',
                        border: '1px solid #FFE2E2',
                        borderRadius: '6px',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Error sx={{ color: '#C10007', fontSize: 18, mt: 0.25 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            {error.entity}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {error.message}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {error.destination && (
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                  fontFamily: 'Inter, sans-serif',
                                }}
                              >
                                {error.destination}
                              </Typography>
                            )}
                            {error.step && (
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                  fontFamily: 'Inter, sans-serif',
                                }}
                              >
                                Step: {error.step}
                              </Typography>
                            )}
                            {error.field && (
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                  fontFamily: 'Inter, sans-serif',
                                }}
                              >
                                Field: {error.field}
                              </Typography>
                            )}
                            {error.row && (
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                  fontFamily: 'Inter, sans-serif',
                                }}
                              >
                                Row: {error.row}
                              </Typography>
                            )}
                          </Box>
                          {error.note && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#C10007',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                mt: 0.5,
                              }}
                            >
                              {error.note}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Warnings Tab */}
              {activeTab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {runData.warnings.map((warning, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 1.5,
                        bgcolor: '#FFFBF0',
                        border: '1px solid #FFEDD4',
                        borderRadius: '6px',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Warning sx={{ color: '#D87000', fontSize: 18, mt: 0.25 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            {warning.entity}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.8125rem',
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {warning.message}
                          </Typography>
                          {warning.count && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#6B7280',
                                fontFamily: 'Inter, sans-serif',
                                mt: 0.5,
                              }}
                            >
                              Affected records: {warning.count}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Metrics Tab */}
              {activeTab === 3 && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: '#0F172A',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {runData.metrics.totalRecords.toLocaleString()}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        Total Records
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#F0FDF4',
                        border: '1px solid #DCFCE7',
                        borderRadius: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: '#008236',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {runData.metrics.successfulRecords.toLocaleString()}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        Successful Records
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#FFF5F5',
                        border: '1px solid #FFE2E2',
                        borderRadius: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: '#C10007',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {runData.metrics.failedRecords}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        Failed Records
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: '#0F172A',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.5,
                        }}
                      >
                        {runData.metrics.successRate}%
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        Success Rate
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default PipelineRunDetails;
